import React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import styled from 'styled-components';

import { useAppSelector } from '@/hooks';
import { selectIsMobileScreenSize } from '@/store/selectors/utilsSelectors';
import { TIMELINE_COLUMN_WIDTH, TIMELINE_COLUMN_WIDTH_MOBILE } from '@/config/variables';

interface IProps {
  content: React.ReactElement;
  parentRef: React.MutableRefObject<HTMLDivElement>;
}

interface IBaseShiftModalCollision {
  collision: Record<'left' | 'right' | 'top' | 'bottom', boolean>;
  x: number;
  y: number;
  scrollX: number;
  isMobile: boolean;
}

const MODAL_WIDTH = 252;
const MODAL_HEIGHT = 197;

const baseShiftModalSelector = createSelector(selectIsMobileScreenSize, isMobileScreenSize => ({
  isMobileScreenSize,
}));

const BaseShiftModal = React.forwardRef<HTMLDivElement, IProps>(({ content, parentRef }, ref): React.ReactElement => {
  const { isMobileScreenSize } = useAppSelector(baseShiftModalSelector);
  const { current } = parentRef;
  const shift = current.getBoundingClientRect();

  const parents = (node: HTMLElement): HTMLElement[] =>
    (node.parentElement ? parents(node.parentElement) : []).concat([node]);

  const scrollX =
    parents(current).find(({ classList }) => classList.contains('my-10') && classList.contains('bg-white'))
      ?.scrollLeft || 0;
  const calendarRect = parents(current)
    .find(el => el.classList.contains('calendar-container'))
    ?.getBoundingClientRect() || { left: 0, right: 0, top: 0, bottom: 0 };

  const collision = {
    left:
      calendarRect.left >
      shift.left - MODAL_WIDTH - scrollX - (isMobileScreenSize ? TIMELINE_COLUMN_WIDTH_MOBILE : TIMELINE_COLUMN_WIDTH),
    right: calendarRect.right < shift.right + MODAL_WIDTH,
    top: calendarRect.top > shift.bottom - MODAL_HEIGHT,
    bottom: calendarRect.bottom < shift.top + MODAL_HEIGHT,
  };

  return (
    <BaseShiftModalContainer
      collision={collision}
      className="base-shift-modal"
      x={shift.x}
      y={shift.y}
      scrollX={scrollX || 0}
      isMobile={isMobileScreenSize}
      onClick={event => event.stopPropagation()}
      ref={ref}
    >
      {content}
    </BaseShiftModalContainer>
  );
});

BaseShiftModal.displayName = 'BaseShiftModal';

const BaseShiftModalContainer = styled.div<IBaseShiftModalCollision>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px;

  background-color: #fff;
  border: 1px solid #e3e3e3;
  border-radius: 7px;

  position: absolute;
  z-index: 100;
  right: calc(100% + 8px);
  cursor: default;

  ${props => {
    let css = '';
    const { left, right, bottom } = props.collision;
    const { x, scrollX, isMobile } = props;

    if (left && !right) {
      css += 'left: calc(100% + 8px);';
    }
    if (!left && right) {
      css += 'right: calc(100% + 8px);';
    }
    if (isMobile && left && right) {
      css += `left: calc(${scrollX - (x - 53 + scrollX)}px) !important; right: auto !important;`;
    }
    if (!isMobile && left && right) {
      css += `left: unset !important; right: auto !important;`;
    }
    css += bottom ? 'bottom: -3px;' : 'top: -3px;';

    return css;
  }}
  [data-day="Mon"] & {
    left: calc(100% + 8px);
    right: unset;
  }

  [data-day='Sun'] & {
    right: calc(100% + 8px);
    left: unset;
  }

  width: ${MODAL_WIDTH}px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);
`;

export { BaseShiftModal };
